import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dropdown,
  Grid,
  GridColumn,
  GridRow,
  Icon,
  Input,
} from 'semantic-ui-react';
import EssentialStar from '../../components/EssentialStar/EssentialStar';
import facebookLogo from "../../components/Layout/facebook.png";
import instagramLogo from "../../components/Layout/instagram.png";
import draxLogo from "../../components/Layout/logo2.png";
import naverLogo from "../../components/Layout/naver.png";
import "./solutions.css";

const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

interface Solution {
  id: number;
  solution: string;
  solution_detail: string;
  isActive: boolean;
  model: number[];
  reg_date: string;
  view_count: number;
  edit_date: string;
  categoryname: string;
  is_notice: boolean;
}

const Solutions = () => {
  const navigate = useNavigate();

  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // 필터 관련 useState상태 정의
  const [selectedCategory, setSelectedCategory] = useState<string>('전체');
  const [searchType, setSearchType] = useState<string>('제목');
  const [searchText, setSearchText] = useState<string>('');
  const [filteredSolutions, setFilteredSolutions] = useState<Solution[]>([]);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  // 최초 및 필터링 값 변경 시 전체 목록/필터링된 목록 계산
  useEffect(() => {
    setFilteredSolutions(solutions);
  }, [solutions]);

  useEffect(() => {
    axios.get(`${apiEndpoint}/receiption/api/solutions/`)
      .then(response => {
        console.log(response.data);
        const resSolution = response.data.map((solution: Solution) => {
          return {
            id: solution.id,
            solution: solution.solution,
            solution_detail: solution.solution_detail,
            isActive: solution.isActive,
            model: solution.model,
            reg_date: new Date(Date.parse(solution.reg_date)).toLocaleDateString(undefined, options).slice(0, -1),
            view_count: solution.view_count,
            edit_date: new Date(Date.parse(solution.reg_date)).toLocaleDateString(undefined, options).slice(0, -1),
            categoryname: solution.categoryname,
            is_notice: solution.is_notice,
          }
        });

        setSolutions(resSolution);

        return resSolution;

      })
      .catch(error => {
        console.error('API 호출 중 오류 발생:', error);
      });
  },[]);

  const genFilterRow = () => {
    // 검색버튼 클릭 시 실행되는 필터 함수
    const handleSearch = () => {
      let filtered = solutions;

      // 카테고리 필터
      if (selectedCategory && selectedCategory !== '전체') {
        filtered = filtered.filter(
          (solution) => solution.categoryname === selectedCategory
        );
      }

      // 검색어 필터
      if (searchText.trim() !== '') {
        const searchValue = searchText.trim().toLowerCase();

        if (searchType === '제목') {
          filtered = filtered.filter(
            (solution) =>
              solution.solution &&
              solution.solution.toLowerCase().includes(searchValue)
          );
        } else if (searchType === '본문') {
          filtered = filtered.filter(
            (solution) =>
              solution.solution_detail &&
              solution.solution_detail.toLowerCase().includes(searchValue)
          );
        } else if (searchType === '본문+제목') {
          filtered = filtered.filter(
            (solution) =>
              (solution.solution &&
                solution.solution.toLowerCase().includes(searchValue)) ||
              (solution.solution_detail &&
                solution.solution_detail.toLowerCase().includes(searchValue))
          );
        }
      }

      setFilteredSolutions(filtered);
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          {/* 카테고리(제품군) 드롭다운 */}
          <div style={{ minWidth: 100 }}>
            <Dropdown
              selection
              clearable={false}
              options={[
                { key: '전체', text: '전체', value: '전체' },
                ...Array.from(new Set(solutions.map(s => s.categoryname).filter(Boolean))).map(cat => ({
                  key: cat,
                  text: cat,
                  value: cat,
                }))
              ]}
              value={selectedCategory}
              onChange={(_, data) => setSelectedCategory(data.value as string)}
              placeholder="제품군"
              style={{ minWidth: 100 }}
            />
          </div>

          {/* 검색(제목/본문 등) 드롭다운 */}
          <div style={{ minWidth: 100 }}>
            <Dropdown
              selection
              clearable={false}
              options={[
                { key: '제목', text: '제목', value: '제목' },
                { key: '본문', text: '본문', value: '본문' },
                { key: '본문+제목', text: '본문+제목', value: '본문+제목' },
              ]}
              value={searchType}
              onChange={(_, data) => setSearchType(data.value as string)}
              placeholder="검색유형"
              style={{ minWidth: 90 }}
            />
          </div>

          {/* 검색 텍스트 입력창 */}
          <Input
            type="text"
            placeholder="검색어 입력"
            value={searchText}
            onChange={(_e, data) => setSearchText(data.value as string)}
            style={{ flex: "1 1 150px", padding: "0.25rem 0.5rem"}}
            onKeyDown={(
              e: React.KeyboardEvent<HTMLInputElement>
            ) => {
              if (e.key === "Enter") {
                if (
                  typeof genContentRow === "function" &&
                  typeof (genContentRow as any).handleSearch === "function"
                ) {
                  (genContentRow as any).handleSearch();
                }
              }
            }}
          />

          {/* 검색 버튼 */}
          <Button
            color='orange'
            onClick={() => {
              // 필터링 함수 직접 실행
              handleSearch();
            }}
          >
            검색
          </Button>
      </div>
    );
  }


  const genContentRow = () => {
    // 필터링 적용 후에는 필터 된 게시글만 랜더링하도록 데이터 분기
    const dataToRender = filteredSolutions && filteredSolutions.length > 0 ? filteredSolutions : solutions;

    return dataToRender.map((solution: Solution) => {
      const id = solution.id;
      const isNotice = solution.is_notice;

      // 강조 스타일 정의: 공지사항 배경/글씨/아이콘 등 추가
      const rowStyle = isNotice
        ? {
            border: '2px solid #ffa500',
            fontWeight: 'bold',
            boxShadow: '0px 1px 6px #ffe08a55',
            cursor: 'pointer',
          }
        : { cursor: 'pointer' };

      const getTitle = () => (
        <div
          className={`align-center content-bold`}
          style={
            isNotice
              ? {
                  color: '#f29900',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  justifyContent: 'center', // 중앙정렬 추가
                  textAlign: 'center',
                  fontSize: '1.4em',
                }
              : {}
          }
        >
          {isNotice && (
            <span
              style={{
                fontSize: '1.1em',
                fontWeight: 'bold',
                color: '#e08c00',
                background: '#fff3cd',
                borderRadius: '6px',
                padding: '1px 8px',
                marginRight: '6px'
              }}
            >
              공지
            </span>
          )}
          {solution.solution}
        </div>
      );

      return (
        <GridRow
          id={`contentRow-${solution.id}`}
          className={`m-2 ${activeIndex === solution.id ? 'active-row' : ''} ${isNotice ? 'notice-row' : ''}`}
          onMouseEnter={() => setActiveIndex(id)}
          onMouseLeave={() => setActiveIndex(null)}
          onClick={() => navigate(`/solutions/${id}`)}
          style={rowStyle}
        >
          <GridColumn width={4}>
            <div className={`align-center ${isNotice ? 'content-notice-category' : 'content-grey smaller'}`}>
              {solution.categoryname}
            </div>
          </GridColumn>
          <GridColumn width={6}>
            {getTitle()}
          </GridColumn>
          <GridColumn width={3}>
            <div className={`align-center ${isNotice ? 'content-notice-date' : 'content-grey smaller'}`}>{solution.reg_date}</div>
          </GridColumn>
          <GridColumn width={3}>
            <div className={`align-center ${isNotice ? 'content-notice-view' : 'content-grey smaller'}`}>{solution.view_count}</div>
          </GridColumn>
        </GridRow>
      );
    });
  }

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center",}}>
      <div style={{ width: "90%", maxWidth: "800px", }}>

        {/* header */}
        <h1 className="align-center my-4" style={{cursor:'pointer'}} onClick={() => navigate('/')}>스스로해결</h1>
        <div className="ui divider"></div>
        <div className="information card background-grey">
        <div>* <span className="emphasis">보증기간이 경과된 제품</span> 또는 보증기간 내라도 <span className="emphasis">고장이 아니거나 사용자 과실</span>인 경우 <span className="emphasis">유상 청구</span>될 수 있습니다.</div>
        <div>* <span className="emphasis">소모품 교체</span> 는 <a href="https://smartstore.naver.com/drax" target="_blank" rel="noreferrer"><span className="emphasis">스마트스토어</span></a>를 통해 간단히 해결할 수 있습니다.<a href="https://smartstore.naver.com/drax" target="_blank" rel="noreferrer">(스마트스토어로 이동하기)</a></div>

        </div>

        {/* filter row */}
        <div className='card'>
          {genFilterRow()}
        </div>

        {/* contents */}
        <div className="information card">
          <Grid className='p-3'>
            <GridRow className='m-2 p-2 grid-header'>
              <GridColumn width={4}>
                <div className='align-center header-small-grey'><strong>제품군</strong></div>
              </GridColumn>
              <GridColumn width={6}>
                <div className='align-center header-small-bold'><strong>제목</strong></div>
              </GridColumn>
              <GridColumn width={3}>
                <div className='align-center header-small-grey'><strong>등록일</strong></div>
              </GridColumn>
              <GridColumn width={3}>
                <div className='align-center header-small-grey'><strong>조회수</strong></div>
              </GridColumn>
            </GridRow>

            {/* content row */}
            {genContentRow()}

            {/* numbering */}
          </Grid>
        </div>

        <div>
          <div className='p-2'>
            <h4>
              <EssentialStar/> 원하시는 제품정보를 못 찾으셨다면 아래 서비스를 이용해보세요.
            </h4>

          </div>
          <Grid className='p-2'>
            <GridRow className='p-4'>
              <GridColumn width={8}>
                <Button color='orange' size='big' fluid onClick={() => {window.open('http://pf.kakao.com/_xifCCT', '_blank')}}><Icon name='talk'></Icon><span style={{fontSize:"1rem"}}>카톡채널</span></Button>
              </GridColumn>
              <GridColumn width={8}>
                <Button color='orange' size='big' fluid onClick={() => {navigate('/customer')}}><Icon name='wrench'></Icon><span style={{fontSize:"1rem"}}>고장접수</span></Button>
              </GridColumn>
            </GridRow>
          </Grid>
        </div>

        {/* footer */}
        <div className="ui divider"/>
        <div className="information2 row" style={{ display: "flex" }}>
          <div
            style={{
              width: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: '0',
              paddingTop: '0',
              paddingBottom: '1rem'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
              <div style={{ flex: '0 0 80%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                <img
                  src={draxLogo}
                  alt="Drax"
                  className="py-1"
                  style={{ width: '100%', height: 'auto', maxWidth: '200px', cursor: 'pointer' }}
                  onClick={() => window.open('https://www.draxfit.com/', '_blank')}
                />
              </div>
              <div style={{ flex: '0 0 20%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20%' }}>
                <img
                  src={naverLogo}
                  alt="Naver"
                  className="px-2"
                  style={{ width: '40px', height: '30px', cursor: 'pointer' }}
                  onClick={() => window.open('https://blog.naver.com/draxfit_official/', '_blank')}
                />
                <img
                  src={instagramLogo}
                  alt="Instagram"
                  className="px-2"
                  style={{ width: '40px', height: '30px', cursor: 'pointer' }}
                  onClick={() => window.open('https://www.instagram.com/draxfit_official/', '_blank')}
                />
                <img
                  src={facebookLogo}
                  alt="Facebook"
                  className="px-2"
                  style={{ width: '40px', height: '30px', cursor: 'pointer' }}
                  onClick={() => window.open('https://www.facebook.com/DRAXFIT/', '_blank')}
                />
              </div>
            </div>
          </div>
          <div
            style={{
              width: "50%",
              textAlign: 'left',
              fontSize: '0.75rem',
              paddingBottom: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{ fontWeight: 'bold', fontSize: '1.5rem', marginBottom: '8px' , marginTop: '27px'}}>주식회사 디랙스</div>
            <div style={{ marginBottom: '0.5px' }}>본사/제1공장 : 경기 안양시 만안구 전파로62번길 26</div>
            <div style={{ marginBottom: '0.5px' }}>제2공장 : 경기 안양시 만안구 전파로 48</div>
            <div style={{ marginBottom: '0.5px' }}>제3공장 : 경기 안양시 만안구 덕천로72번길 85</div>
            <div style={{ marginBottom: '0.5px' }}>사업자등록번호 123-81-70002</div>
            <div style={{ marginBottom: '0.5px' }}>TEL: 031-459-8530 / FAX: 031-459-8531</div>
          </div>
        </div>
        {/* footer */}
      </div>
    </div>
  )
}

export default Solutions;